import type { ProductDecorationRequest } from "../../services/mockupGeneration";
import { getImageSrc, isUUID } from "../../utils";
import type { IFile } from "../../interfaces/File";
import type { PsConfigLocationOption } from "./decorationModel";
import { safeString, type SelectOption } from "./mockupUtils";

/**
 * Removes the "Location N::" prefix that some item payloads include in location titles.
 */
export function stripLocationPrefix(raw: string): string {
  return raw.replace(/^Location\s+\d+::\s*/i, "").trim();
}

export function buildSelectValue<T extends { value: string }>(
  options: readonly T[],
  value: string | null
): T | undefined {
  if (!value) return undefined;
  return options.find((o) => o.value === value);
}

export function firstNonEmptyString(...values: unknown[]): string {
  for (const v of values) {
    const s = safeString(v).trim();
    if (s.length > 0) return s;
  }
  return "";
}

export interface SelectOptionWithDecoration extends SelectOption {
  decoration: ProductDecorationRequest;
}

export function applyImprintLabelsToExistingOptions<T extends SelectOptionWithDecoration>(
  options: readonly T[],
  imprintOptions: readonly SelectOption[]
): T[] {
  if (!options.length) return options.slice();
  const imprintLabelById = new Map(imprintOptions.map((o) => [o.value, o.label]));

  return options.map((o, idx) => {
    const imprintId = o.decoration.imprint_id ?? null;
    if (!imprintId) return o;
    const imprintLabel = imprintLabelById.get(imprintId) ?? null;
    const fallbackLabel = options.length > 1 ? `Imprint ${idx + 1}` : "Imprint";

    const shouldReplaceLabel =
      o.label === imprintId ||
      o.label === "No Name" ||
      (isUUID(o.label) && o.label === imprintId);

    if (!shouldReplaceLabel) return o;

    const nextLabel = imprintLabel ?? fallbackLabel;

    return {
      ...o,
      label: nextLabel,
      decoration: {
        ...o.decoration,
        decoration_name: nextLabel,
      },
    } as T;
  });
}

export function applyPsConfigLabelsToExistingOptions<T extends SelectOptionWithDecoration>(
  options: readonly T[],
  psConfigLocations: PsConfigLocationOption[]
): T[] {
  if (!options.length || !psConfigLocations.length) return options.slice();

  return options.map((o) => {
    const extLocationIdRaw = o.decoration.ext_location_id ?? null;
    const extDecorationIdRaw = o.decoration.ext_decoration_id ?? null;
    if (extLocationIdRaw == null || extDecorationIdRaw == null) return o;

    const extLocationId = safeString(extLocationIdRaw);
    const extDecorationId = safeString(extDecorationIdRaw);

    const cfgLoc = psConfigLocations.find((l) => safeString(l.ps_location_id) === extLocationId) ?? null;
    if (!cfgLoc) return o;

    const cfgDec =
      (cfgLoc.decorations ?? []).find((d) => safeString(d.ps_decoration_id ?? d.decoration_id ?? "") === extDecorationId) ?? null;
    if (!cfgDec) return o;

    // Some PS payloads send empty strings; treat them as missing.
    const cfgName = firstNonEmptyString(cfgDec.decoration_name, cfgDec.decoration, cfgDec.name);
    if (!cfgName) return o;

    const shouldReplace =
      o.label === "No Name" ||
      isUUID(o.label) ||
      o.label === safeString(o.decoration.imprint_id ?? "") ||
      o.label === safeString(extDecorationId);

    if (!shouldReplace) return o;

    return {
      ...o,
      label: cfgName,
      decoration: {
        ...o.decoration,
        decoration_name: cfgName,
        imprint_name: cfgName,
      },
    } as T;
  });
}

export type ArtworkLibrarySelectedFile = Partial<IFile> & {
  // Artwork-library payloads aren't guaranteed to be full `IFile`s.
  file_id?: string | null;
  original_url?: string | null;
  // Some artwork payloads include these direct URLs.
  file_name_large?: string | null;
  file_name_original?: string | null;
};

export function resolveArtworkUrl(file: ArtworkLibrarySelectedFile): string | null {
  const viaUtils =
    getImageSrc(file as unknown as Record<string, unknown>, "large") ||
    getImageSrc(file as unknown as Record<string, unknown>, "medium") ||
    getImageSrc(file as unknown as Record<string, unknown>, "original") ||
    null;
  if (viaUtils) return viaUtils;

  const direct =
    (typeof file.file_name_large === "string" && file.file_name_large) ||
    (typeof file.file_name_original === "string" && file.file_name_original) ||
    (typeof file.original_url === "string" && file.original_url) ||
    null;
  return direct;
}

